import { createAction } from "@reduxjs/toolkit";
import { amRoutes, pencilerRoles, ucEndpoints, urbancanvasRoles } from "../../config/config";
import { AppDispatch, RootState } from "../../reducers";
import { history } from "../../store";
import {
  getPencilerStartProjectsStatusFailed,
  getPencilerStartProjectsStatusSuccess,
  getRoleInvitesStatusFailed,
  gettingPencilerStartProjectsStatus,
  gettingRoleInvitesStatus,
  gettingUsers,
  gettingUsersByRole,
  getUsersByRoleFailed,
  getUsersByRoleSuccess,
  getUsersFailed,
  inviteUserToAccountFailed,
  removeOrganizationContact,
  setAccounting,
  setFineGrainedPermissions,
  setLimits,
  setOrganization,
  setOrganizationContact,
  setOrganizationsAdministators,
  setOrganizationsRoles,
  setPermissions,
  setRoleInvitesStatus,
  setUsers
} from "../../store/accounts";
import { capitalize } from "../../utils/utils";
import { apiCallBegan } from "../middlwares/api";
import * as actionTypes from "./actionTypes";

export const submitForm = createAction(actionTypes.SUBMITTING_ACCOUNT);
export const submitSuccess = createAction(actionTypes.SUBMIT_SUCCEEDED);
export const createAccountFailed = createAction(actionTypes.CREATE_ACCOUNT_FAILED);

export const createAccount = (newAccountFields: Record<string, any>) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const { system } = getState().newAccount;
    const newAccount = {
      user: {
        name: `${newAccountFields.firstName} ${newAccountFields.lastName}`,
        email: newAccountFields.email
      },
      organization: {
        name: newAccountFields.organization.name,
        city: newAccountFields.organization.city,
        region: newAccountFields.organization.region
      },
      demo: newAccountFields.demo,
      system: system
    };
    return (dispatch: AppDispatch) =>
      dispatch(
        apiCallBegan({
          url: `${ucEndpoints.createClientAccount}`,
          method: "post",
          data: newAccount,
          onStart: submitForm,
          onSuccess: () => (dispatch: AppDispatch) => {
            dispatch(submitSuccess());
            history.push({
              pathname: amRoutes.login,
              search: "?created_account_success=true"
            });
          },
          onFailure: createAccountFailed
        })
      );
  };
};

export const getUsers = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.thisOrganizationUsers}`,
      method: "get",
      onStart: gettingUsers,
      onSuccess: setUsers,
      onFailure: getUsersFailed
    })
  );

export const getUsersByRole = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.thisOrganizationUsers}`,
      method: "get",
      params: { groupBy: "role" },
      onStart: gettingUsersByRole,
      onSuccess: getUsersByRoleSuccess,
      onFailure: getUsersByRoleFailed
    })
  );

export const refreshOrganization = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.thisOrganization}`,
      method: "get",
      onSuccess: setOrganization
    })
  );

export const inviteUserToAccount = (
  firstName: string,
  lastName: string,
  email: string,
  roleCode: string
) => {
  const system = roleCode.includes(pencilerRoles.prefix)
    ? pencilerRoles.system
    : urbancanvasRoles.system;
  return (dispatch: AppDispatch) =>
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.inviteUser}`,
        method: "post",
        data: {
          email: email,
          roleCode: roleCode,
          system: system
        },
        // Note: missing onStart (possibly call to startInviteUserToAccountSubmit)
        onSuccess: getUsers,
        onSuccessMessage: "User successfully invited!",
        onFailure: inviteUserToAccountFailed
      })
    );
};

/**
 * Get Penciler Start Projects status form UC and updates pencilerStartProjectsStatus
 *
 * @return {ThunkAction}
 */
export const getPencilerStartProjectsStatus = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.pencilerStartProjectsStatus}`,
      method: "get",
      onStart: gettingPencilerStartProjectsStatus,
      onSuccess: getPencilerStartProjectsStatusSuccess,
      onFailure: getPencilerStartProjectsStatusFailed
    })
  );

/**
 * Get invites by role status.
 *
 * @param {String} roleCode       - Role code.
 */
export const getRoleInvitesStatus = (roleCode: string) => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.userInviteStatusByRole}/${roleCode}`,
      method: "get",
      onStart: gettingRoleInvitesStatus,
      onSuccess: setRoleInvitesStatus,
      onFailure: getRoleInvitesStatusFailed
    })
  );

/**
 * Get limits and accounting.
 */
export const getLimitsAndAccounting = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.usersAccounting}`,
      method: "get",
      onSuccess: setAccounting
    })
  );
  return dispatch(
    apiCallBegan({
      url: `${ucEndpoints.usersLimits}`,
      method: "get",
      onSuccess: setLimits
    })
  );
};

/**
 * Get permissions
 */
export const getPermissions = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.permissions}`,
      method: "get",
      onSuccess: setPermissions
    })
  );

/**
 * Get permissions
 */
export const getFineGrainedPermissions = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.fineGrainedPermissions}`,
      method: "get",
      onSuccess: setFineGrainedPermissions
    })
  );

/**
 * Get organization roles
 */
export const getOrganizationsRoles = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizationsRoles}`,
      method: "get",
      onSuccess: setOrganizationsRoles
    })
  );

/**
 * Get organization roles
 */
export const getAdministrators = () => {
  return (dispatch: AppDispatch) =>
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.organizationsAdministrators}`,
        method: "get",
        onSuccess: setOrganizationsAdministators
      })
    );
};

/**
 * Put organization roles
 */
export const editOrganizationsRoles = (roles: Record<string, any>) => (dispatch: AppDispatch) => {
  const rolesKeys = Object.keys(roles);
  const mappedRoles = rolesKeys.map(key => Object.assign({ id: parseInt(key) }, roles[key]));
  return dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizationsRoles}`,
      method: "put",
      data: mappedRoles,
      onSuccess: setOrganizationsRoles
    })
  );
};

/**
 * Edit organization information
 */
export const editOrganization = (organization: Record<string, any>) => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizations}/this`,
      method: "put",
      data: organization,
      onSuccess: setOrganization,
      onSuccessMessage: "Organization infomation successfully saved!",
      onFailureMessage: "Error while saving organization information"
    })
  );

/**
 * Remove organization information contact
 */
export const removeOrganizationsContact = (id: number) => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizationsContacts}/${id}`,
      method: "delete",
      onSuccess: removeOrganizationContact,
      onSuccessMessage: "Contact successfully deleted!",
      onFailureMessage: "Error while deleting contact"
    })
  );

/**
 * Edit organization contact information
 */
export const createOrganizationsContact = (contact: Record<string, any>) => (
  dispatch: AppDispatch
) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizationsContacts}`,
      method: "post",
      data: contact,
      onSuccess: setOrganizationContact,
      onSuccessMessage: "Contact successfully created!",
      onFailureMessage: "Error while creating contact"
    })
  );
/**
 * Edit organization contact information
 */
export const editOrganizationsContact = (id: number, contact: Record<string, any>) => (
  dispatch: AppDispatch
) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.organizationsContacts}/${id}`,
      method: "put",
      data: contact,
      onSuccess: setOrganizationContact,
      onSuccessMessage: "Contact information successfully saved!",
      onFailureMessage: "Error while saving contact information"
    })
  );

/**
 * Get penciler code from a mapped role name.
 * (mapped by the remapUsersByRole function)
 *
 * @param {String} name     - Role name to process.
 * @return {String}         - Returns role code.
 */
export const pencilerMappedRoleNameToCode = (name: string) => {
  if (name === "Administrator") name = "Admin";
  return `${pencilerRoles.prefix}${name}`.toUpperCase();
};

/**
 * Parses limits and accounting raw object in order to return a well defined structure
 *
 * @param {Object} limits           - Account limits
 * @param {Object} accounting       - Account accounting
 * @returns {{database: {total: *, value: *}, longSimulationRuns: {total: *, value: *},
 * shortSimulationRuns: {total: *, value: boolean}}}
 */
export const processRawLimitsAndAccounting = (
  limits: Record<string, any>,
  accounting: Record<string, any>
) => ({
  database: {
    total: (limits.limits["region_size"]["total_size"] / (1024 * 1024)).toFixed(0),
    value: (accounting["region_size"] / (1024 * 1024)).toFixed(0)
  },
  longSimulationRuns: {
    total: limits.limits.simulations["large_amount"],
    value: accounting.simulations.large
  },
  shortSimulationRuns: {
    total: limits.limits.simulations["partial_amount"],
    value: accounting.simulations.partial
  }
});

/**
 * Removes namePrefix from penciler role name.
 *
 * @param {String} roleName       - Role name.
 */
export const remapRoleNameFromRoleName = (roleName: string) => {
  const words = roleName.split(" ");
  const mappedWords = words.map(word =>
    word.toLowerCase() === "admin" ? "Administrator" : capitalize(word)
  );
  return mappedWords.join(" ");
};
